import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import WorkoutService from '../../services/workoutService';

const WorkoutHistory = () => {
  const { token } = useContext(AuthContext);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchWorkouts();
  }, [token, currentPage, filter, searchTerm, sortBy, sortOrder]);

  const fetchWorkouts = async () => {
    try {
      setLoading(true);
      
      const queryParams = {};
      queryParams.page = currentPage;
      queryParams.limit = 10;
      queryParams.sortBy = sortBy;
      queryParams.sortOrder = sortOrder;
      
      if (filter !== 'all') {
        queryParams.filter = filter;
      }
      
      if (searchTerm) {
        queryParams.search = searchTerm;
      }
      
      const data = await WorkoutService.getAllWorkouts(token, queryParams);
      
      if (data.success) {
        setWorkouts(data.workouts || []);
        setTotalPages(data.pagination.totalPages || 1);
      } else {
        toast.error(data.message || 'Failed to fetch workouts');
      }
    } catch (error) {
      console.error('Error fetching workouts:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorkout = async (workoutId) => {
    if (!window.confirm('Are you sure you want to delete this workout?')) {
      return;
    }
    
    try {
      const data = await WorkoutService.deleteWorkout(workoutId, token);
      
      if (data.success) {
        toast.success('Workout deleted successfully');
        fetchWorkouts();
      } else {
        toast.error(data.message || 'Failed to delete workout');
      }
    } catch (error) {
      console.error('Error deleting workout:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    const [newSortBy, newSortOrder] = value.split('-');
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1);
  };

  if (loading && workouts.length === 0) {
    return <div className="loading">Loading workout history...</div>;
  }

  return (
    <div className="workout-history">
      <h1>Workout History</h1>
      
      {/* Filters and Search */}
      <div className="filters-container">
        <div className="filter-group">
          <label htmlFor="filter">Filter:</label>
          <select
            id="filter"
            value={filter}
            onChange={handleFilterChange}
          >
            <option value="all">All Workouts</option>
            <option value="last7days">Last 7 Days</option>
            <option value="last30days">Last 30 Days</option>
            <option value="last3months">Last 3 Months</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="sortBy">Sort By:</label>
          <select
            id="sortBy"
            value={`${sortBy}-${sortOrder}`}
            onChange={handleSortChange}
          >
            <option value="date-desc">Date (Newest First)</option>
            <option value="date-asc">Date (Oldest First)</option>
            <option value="title-asc">Title (A-Z)</option>
            <option value="title-desc">Title (Z-A)</option>
          </select>
        </div>
        
        <form className="search-form" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search workouts..."
          />
          <button type="submit" className="btn btn-primary btn-sm">
            Search
          </button>
        </form>
      </div>
      
      {/* Workouts List */}
      {workouts.length === 0 ? (
        <div className="no-workouts">
          <p>No workouts found.</p>
          <Link to="/workout" className="btn btn-primary">
            Start a Workout
          </Link>
        </div>
      ) : (
        <div className="workouts-list">
          {workouts.map((workout) => (
            <div key={workout._id} className="workout-card">
              <div className="workout-header">
                <h2>{workout.title}</h2>
                <span className="workout-date">{formatDate(workout.date)}</span>
              </div>
              
              <div className="workout-details">
                <p>
                  <strong>Exercises:</strong> {workout.exercises.length}
                </p>
                <p>
                  <strong>Total Sets:</strong>{' '}
                  {workout.exercises.reduce((total, ex) => total + ex.sets.length, 0)}
                </p>
              </div>
              
              {workout.notes && (
                <div className="workout-notes">
                  <p><strong>Notes:</strong> {workout.notes}</p>
                </div>
              )}
              
              <div className="workout-actions">
                <Link to={`/history/${workout._id}`} className="btn btn-primary btn-sm">
                  View Details
                </Link>
                <button
                  onClick={() => handleDeleteWorkout(workout._id)}
                  className="btn btn-danger btn-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn btn-light btn-sm"
          >
            Previous
          </button>
          
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn btn-light btn-sm"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkoutHistory;
